// ============================================================
// ClientUsersTab.js
// Users tab inside <ClientDetailPage>. Lists ClientUsers for one
// Client with role filter and pagination. Row click → user detail
// (ComingSoonPage stub for now).
//
// Backend reference:
//   backend/func-carehub-api/src/functions/admin-clients-users.js
//
// Backend supports roleKey filter (manager|carer|admin|auditor) and
// page/pageSize pagination. Search by email is not yet supported on
// the backend — once the backend adds it, surface a search input.
//
// PENDING INVITATIONS STRIP (2026-09-03). A ClientUsers row is created
// ONLY at redemption (or federated JIT sign-in), so a person who has
// been invited but has not yet accepted is invisible on this tab —
// which is exactly where an operator looks after provisioning a
// trial and asks "why isn't the requester listed?". The strip above
// the table reads the same invitations list the Invitations page
// uses, filtered to this client and to the two states an operator
// can act on (pending / expired), with a one-click Resend. The users
// table itself is unchanged: a placeholder ClientUsers row was
// deliberately NOT introduced (it would change what "active user"
// means for billing headcounts and every active-user query).
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { UserPlus, MailWarning } from 'lucide-react';
import DataTable from '../components/DataTable';
import { RoleBadge, ActiveStatusBadge, SetupRequiredBadge, InvitationStatusBadge } from '../components/StatusBadge';
import { listAdminClientUsers } from '../api/adminClients';
import { listInvitations, resendInvitation } from '../api/invitations';
import { translateError } from '../utils/errorMessages';
import { formatDate, formatRelativeFuture } from '../utils/format';
import { toast } from '../components/Toaster';
import EmailDeliveryNote from '../components/EmailDeliveryNote';
import useDelayedRefresh from '../hooks/useDelayedRefresh';
import './ClientUsersTab.css';

// The invitations list endpoint filters on ONE status; the strip wants
// two (pending + expired), so it pulls the client's invitations
// unfiltered (200 is far above any client's count) and keeps the
// actionable ones client-side.
const OPEN_INVITATION_STATUSES = new Set(['pending', 'expired']);

function PendingInvitationsStrip({ clientId, reloadKey }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await listInvitations({ clientId, limit: 200 });
      const all = (res && res.invitations) || [];
      setRows(all.filter((inv) => OPEN_INVITATION_STATUSES.has(inv.status)));
    } catch {
      // Best-effort: the strip is a convenience over the users table;
      // a failed read renders nothing rather than an error the users
      // table already has its own surface for.
      setRows([]);
    } finally {
      setLoaded(true);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load, reloadKey]);
  const scheduleDeliveryRefresh = useDelayedRefresh(load);

  async function handleResend(inv) {
    setResendingId(inv.invitationId);
    try {
      await resendInvitation(inv.invitationId);
      toast.success(`Invitation re-sent to ${inv.email}. The new link is valid for 30 days.`);
      await load();
      // A refusal by their mail server arrives seconds after the send is
      // accepted, so re-read once more to surface it.
      scheduleDeliveryRefresh();
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setResendingId(null);
    }
  }

  if (!loaded || rows.length === 0) return null;

  return (
    <section className="client-users__pending" aria-label="Pending invitations">
      <div className="client-users__pending-head">
        <MailWarning size={16} aria-hidden="true" />
        <span>
          {rows.length === 1
            ? '1 person has been invited but has not accepted yet.'
            : `${rows.length} people have been invited but have not accepted yet.`}
          {' '}They appear here, not in the users list, until they accept.
        </span>
        <Link to="/invitations" className="client-users__pending-link">All invitations</Link>
      </div>
      <ul className="client-users__pending-list">
        {rows.map((inv) => (
          <li key={inv.invitationId} className="client-users__pending-row">
            <div className="client-users__pending-who">
              <span className="client-users__email-text">{inv.email}</span>
              {inv.displayName && <span className="client-users__display-name">{inv.displayName}</span>}
            </div>
            <RoleBadge roleKey={inv.roleKey} />
            <InvitationStatusBadge status={inv.status} />
            <span className="client-users__muted">
              {inv.status === 'expired'
                ? `Expired ${formatDate(inv.expiresAt)}`
                : `Expires ${formatRelativeFuture(inv.expiresAt)}`}
              {inv.reminderCount > 0 && ` · reminded ${inv.reminderCount === 1 ? 'once' : `${inv.reminderCount} times`}`}
            </span>
            <button
              type="button"
              className="client-users__pending-resend"
              onClick={() => handleResend(inv)}
              disabled={resendingId === inv.invitationId}
            >
              {resendingId === inv.invitationId ? 'Sending…' : 'Resend'}
            </button>
            <EmailDeliveryNote lastEmail={inv.lastEmail} className="client-users__pending-delivery" />
          </li>
        ))}
      </ul>
    </section>
  );
}

const DEFAULT_PAGE_SIZE = 50;

const ROLE_FILTER_OPTIONS = [
  { value: '',        label: 'All roles' },
  { value: 'manager', label: 'Manager' },
  { value: 'carer',   label: 'Carer' },
  { value: 'admin',   label: 'Admin' },
  { value: 'auditor', label: 'Auditor' },
];

function parsePageParam(raw, fallback) {
  if (raw == null) return fallback;
  const n = parseInt(raw, 10);
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

export default function ClientUsersTab({ clientId }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page     = parsePageParam(searchParams.get('page'), 1);
  const pageSize = parsePageParam(searchParams.get('pageSize'), DEFAULT_PAGE_SIZE);
  const roleKey  = searchParams.get('roleKey') || '';

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize };
      if (roleKey) params.roleKey = roleKey;
      const res = await listAdminClientUsers(clientId, params);
      setItems(res.items || []);
      setTotalCount(res.totalCount || 0);
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [clientId, page, pageSize, roleKey]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleRoleChange(value) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('roleKey', value); else next.delete('roleKey');
      next.delete('page');
      return next;
    });
  }

  function handlePageChange(nextPage) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextPage === 1) next.delete('page'); else next.set('page', String(nextPage));
      return next;
    });
  }

  const columns = [
    {
      key: 'email',
      label: 'Email',
      render: (row) => (
        <div className="client-users__email">
          <span className="client-users__email-text">{row.email}</span>
          {row.displayName && row.displayName !== row.email && (
            <span className="client-users__display-name">{row.displayName}</span>
          )}
        </div>
      ),
    },
    {
      key: 'roleKey',
      label: 'Role',
      render: (row) => <RoleBadge roleKey={row.roleKey} />,
      width: '120px',
    },
    {
      key: 'setup',
      label: 'Setup',
      render: (row) => <SetupRequiredBadge requiresAgentSetup={row.requiresAgentSetup} />,
      width: '140px',
    },
    {
      key: 'isActive',
      label: 'Status',
      render: (row) => <ActiveStatusBadge isActive={row.isActive} />,
      width: '100px',
    },
    {
      key: 'accessExpiresAt',
      label: 'Access expires',
      render: (row) => (
        <span className="client-users__muted">
          {row.accessExpiresAt ? formatDate(row.accessExpiresAt) : '—'}
        </span>
      ),
      width: '160px',
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (row) => (
        <span className="client-users__muted">{formatDate(row.createdAt)}</span>
      ),
      width: '140px',
    },
  ];

  function handleRowClick(row) {
    navigate(`/users/${row.clientUserId}`);
  }

  const isFiltered = Boolean(roleKey);
  const emptyMessage = isFiltered
    ? 'No users match this filter.'
    : 'No users in this Client yet. Invite the first user to get started.';

  return (
    <div className="client-users">
      <PendingInvitationsStrip clientId={clientId} reloadKey={items.length} />

      <div className="client-users__toolbar">
        <label className="client-users__role-filter">
          <span className="client-users__role-label">Role</span>
          <select
            value={roleKey}
            onChange={(e) => handleRoleChange(e.target.value)}
            className="client-users__role-select"
          >
            {ROLE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="client-users__invite"
          onClick={() => navigate(`/invitations/new?clientId=${clientId}`)}
        >
          <UserPlus size={16} />
          Invite User
        </button>
      </div>

      <DataTable
        data={items}
        columns={columns}
        rowKey={(row) => row.clientUserId}
        onRowClick={handleRowClick}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        loading={loading}
        error={error}
        onRetry={fetchData}
        emptyMessage={emptyMessage}
        emptyAction={
          !isFiltered && !loading && !error && items.length === 0 ? (
            <button
              type="button"
              className="client-users__invite-empty"
              onClick={() => navigate(`/invitations/new?clientId=${clientId}`)}
            >
              <UserPlus size={16} />
              Invite first user
            </button>
          ) : null
        }
      />
    </div>
  );
}
